import { useState, useMemo } from 'react'
import { motion } from 'framer-motion'
import {
  ChevronRight,
  ChevronLeft,
  MessageSquare,
  Share2,
  ArrowLeft,
  BookText,
  Pencil,
  Quote,
  Clock,
  Palette,
  Search,
} from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import { cn, normalizeString } from '@/lib/utils'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { mockBiblia } from '@/mocks/biblia.mock'

interface BibliaVerse {
  number: number
  text: string
}

interface BibliaChapter {
  number: number
  verses: BibliaVerse[]
}

interface BibliaBook {
  name: string
  chapters: BibliaChapter[]
  totalVerses: number
  hasAnnotations: boolean
  hasCitations: boolean
  lastReadChapter?: number
  lastReadVerse?: number
}

interface BibliaTestament {
  name: 'Antigo Testamento' | 'Novo Testamento'
  books: BibliaBook[]
}

const bibliaData = mockBiblia as BibliaTestament[]

type FilteredTestament = Omit<BibliaTestament, 'books'> & { books: BibliaBook[] }

interface AnnotationTarget {
  bookName: string
  chapterNumber: number
  verseNumber: number
}

interface Annotation {
  text: string
  bgColor: string // Tailwind CSS class for background color, e.g., 'bg-yellow-100'
  textColor: string // Tailwind CSS class for text color, e.g., 'text-yellow-800'
  darkBgColor: string // Tailwind CSS class for dark mode background color
  darkTextColor: string // Tailwind CSS class for dark mode text color
}

const ANNOTATION_COLORS = [
  {
    name: 'Amarelo',
    bgColor: 'bg-yellow-100',
    textColor: 'text-yellow-800',
    darkBgColor: 'dark:bg-yellow-900',
    darkTextColor: 'dark:text-yellow-200',
  },
  {
    name: 'Verde',
    bgColor: 'bg-green-100',
    textColor: 'text-green-800',
    darkBgColor: 'dark:bg-green-900',
    darkTextColor: 'dark:text-green-200',
  },
  {
    name: 'Vermelho',
    bgColor: 'bg-red-100',
    textColor: 'text-red-800',
    darkBgColor: 'dark:bg-red-900',
    darkTextColor: 'dark:text-red-200',
  },
  {
    name: 'Azul',
    bgColor: 'bg-blue-100',
    textColor: 'text-blue-800',
    darkBgColor: 'dark:bg-blue-900',
    darkTextColor: 'dark:text-blue-200',
  },
  {
    name: 'Transparente',
    bgColor: 'bg-transparent',
    textColor: 'text-foreground',
    darkBgColor: 'dark:bg-transparent',
    darkTextColor: 'dark:text-foreground',
  },
]

export function BibliaPage() {
  const [selectedBook, setSelectedBook] = useState<BibliaBook | null>(null)
  const [selectedChapter, setSelectedChapter] = useState<BibliaChapter | null>(null)
  const [showAnnotationModal, setShowAnnotationModal] = useState(false)
  const [currentAnnotationText, setCurrentAnnotationText] = useState('')
  const [currentAnnotationColorOption, setCurrentAnnotationColorOption] = useState(
    ANNOTATION_COLORS[0]
  )
  const [currentAnnotationTarget, setCurrentAnnotationTarget] = useState<AnnotationTarget | null>(
    null
  )
  const [searchTerm, setSearchTerm] = useState('')

  // Function to get annotation from localStorage
  const getAnnotation = (target: AnnotationTarget): Annotation | null => {
    const key = `biblia_annotation_${target.bookName}_${target.chapterNumber}_${target.verseNumber}`
    const stored = localStorage.getItem(key)
    if (!stored) return null

    try {
      // Try to parse as JSON (new format)
      const parsed = JSON.parse(stored)
      // Basic validation to ensure it's the expected Annotation object
      if (
        typeof parsed === 'object' &&
        parsed !== null &&
        'text' in parsed &&
        'bgColor' in parsed &&
        'textColor' in parsed &&
        'darkBgColor' in parsed &&
        'darkTextColor' in parsed
      ) {
        return parsed as Annotation
      }
    } catch {
      // If JSON.parse fails, assume it's the old plain text format
      // Convert old format to new format with default colors
      return {
        text: stored,
        bgColor: ANNOTATION_COLORS[0].bgColor,
        textColor: ANNOTATION_COLORS[0].textColor,
        darkBgColor: ANNOTATION_COLORS[0].darkBgColor,
        darkTextColor: ANNOTATION_COLORS[0].darkTextColor,
      }
    }
    return null
  }

  // Function to save annotation to localStorage
  const saveAnnotation = (target: AnnotationTarget, annotation: Annotation) => {
    const key = `biblia_annotation_${target.bookName}_${target.chapterNumber}_${target.verseNumber}`
    if (annotation.text.trim() === '' && annotation.bgColor === 'bg-transparent') {
      localStorage.removeItem(key)
    } else {
      localStorage.setItem(key, JSON.stringify(annotation))
    }
  }

  const handleBookSelect = (book: BibliaBook) => {
    setSelectedBook(book)
    setSelectedChapter(book.chapters[0] || null) // Select first chapter by default
  }

  const handleChapterChange = (direction: 'prev' | 'next') => {
    if (!selectedBook || !selectedChapter) return

    const currentChapterIndex = selectedBook.chapters.findIndex(
      (c) => c.number === selectedChapter.number
    )

    if (direction === 'prev' && currentChapterIndex > 0) {
      setSelectedChapter(selectedBook.chapters[currentChapterIndex - 1])
    } else if (direction === 'next' && currentChapterIndex < selectedBook.chapters.length - 1) {
      setSelectedChapter(selectedBook.chapters[currentChapterIndex + 1])
    }
  }

  const handleBackToBooks = () => {
    setSelectedBook(null)
    setSelectedChapter(null)
    setSearchTerm('') // Clear search term when going back to book list
  }

  const handleCiteVerse = () => {
    if (selectedBook && selectedChapter && selectedChapter.verses.length > 0) {
      const firstVerse = selectedChapter.verses[0]
      const citation = `${selectedBook.name} ${selectedChapter.number}:${firstVerse.number} - ${firstVerse.text}`
      navigator.clipboard.writeText(citation)
      alert('Citação copiada para a área de transferência!') // Placeholder for a toast notification
    }
  }

  const handleOpenAnnotationModal = (verse: BibliaVerse) => {
    if (!selectedBook || !selectedChapter) return

    const target: AnnotationTarget = {
      bookName: selectedBook.name,
      chapterNumber: selectedChapter.number,
      verseNumber: verse.number,
    }
    setCurrentAnnotationTarget(target)
    const existingAnnotation = getAnnotation(target)
    setCurrentAnnotationText(existingAnnotation?.text || '')
    setCurrentAnnotationColorOption(
      ANNOTATION_COLORS.find((c) => c.bgColor === existingAnnotation?.bgColor) ||
        ANNOTATION_COLORS[0]
    )
    setShowAnnotationModal(true)
  }

  const handleSaveAnnotation = () => {
    if (currentAnnotationTarget) {
      saveAnnotation(currentAnnotationTarget, {
        text: currentAnnotationText,
        bgColor: currentAnnotationColorOption.bgColor,
        textColor: currentAnnotationColorOption.textColor,
        darkBgColor: currentAnnotationColorOption.darkBgColor,
        darkTextColor: currentAnnotationColorOption.darkTextColor,
      })
      setShowAnnotationModal(false)
      setCurrentAnnotationText('')
      setCurrentAnnotationColorOption(ANNOTATION_COLORS[0])
      setCurrentAnnotationTarget(null)
    }
  }

  const handleCancelAnnotation = () => {
    setShowAnnotationModal(false)
    setCurrentAnnotationText('')
    setCurrentAnnotationColorOption(ANNOTATION_COLORS[0])
    setCurrentAnnotationTarget(null)
  }

  const filteredBooks = useMemo((): FilteredTestament[] => {
    if (!searchTerm) {
      return bibliaData.map((testament) => ({
        ...testament,
        books: [...testament.books],
      }))
    }

    const normalizedSearchTerm = normalizeString(searchTerm)
    const results: FilteredTestament[] = []

    for (const testament of bibliaData) {
      const matchingBooks: BibliaBook[] = testament.books.filter((book) =>
        normalizeString(book.name).includes(normalizedSearchTerm)
      )

      if (matchingBooks.length > 0) {
        results.push({
          ...testament,
          books: matchingBooks,
        })
      }
    }

    return results
  }, [searchTerm])

  const filteredVerses = useMemo<BibliaVerse[]>(() => {
    if (!selectedChapter || !searchTerm) return selectedChapter?.verses || []

    const normalizedSearchTerm = normalizeString(searchTerm)

    return selectedChapter.verses.filter((verse) =>
      normalizeString(verse.text).includes(normalizedSearchTerm)
    )
  }, [selectedChapter, searchTerm])

  const handleAnnotateFirstVerse = () => {
    if (!selectedChapter || selectedChapter.verses.length === 0) {
      return
    }

    handleOpenAnnotationModal(selectedChapter.verses[0])
  }

  return (
    <div className="flex h-full flex-col p-4 lg:p-8">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-6 space-y-1"
      >
        <h1 className="text-3xl font-bold tracking-tight md:text-4xl">Bíblia Sagrada</h1>
        <p className="text-muted-foreground">
          Navegue pelos livros, capítulos e versículos da Bíblia.
        </p>
      </motion.div>

      <div className="flex-1 overflow-hidden">
        {!selectedBook ? (
          // Book List View
          <motion.div
            key="book-list"
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            transition={{ duration: 0.3 }}
            className="h-full"
          >
            <Card className="h-full">
              <CardHeader>
                <CardTitle>Livros da Bíblia</CardTitle>
                <CardDescription>Selecione um livro para começar a ler.</CardDescription>
              </CardHeader>
              <CardContent className="h-[calc(100%-100px)]">
                <div className="relative mb-4">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    placeholder="Buscar livro..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <ScrollArea className="h-[calc(100%-60px)] pr-4">
                  {filteredBooks.length === 0 ? (
                    <p className="text-center text-muted-foreground">Nenhum livro encontrado.</p>
                  ) : (
                    filteredBooks.map((testament) => (
                      <div key={testament.name} className="mb-6">
                        <h3 className="mb-3 text-lg font-semibold text-primary">
                          {testament.name}
                        </h3>
                        <div className="grid grid-cols-2 gap-2 md:grid-cols-3 lg:grid-cols-4">
                          {testament.books.map((book) => {
                            const normalizedBook = book as BibliaBook
                            const {
                              name: bookName,
                              totalVerses,
                              hasAnnotations,
                              hasCitations,
                              lastReadChapter,
                              lastReadVerse,
                            } = normalizedBook

                            const selectedBookName = (selectedBook as BibliaBook | null)?.name
                            const isSelected = selectedBookName === bookName

                            return (
                              <Button
                                key={bookName}
                                variant="outline"
                                className={cn(
                                  'relative flex h-auto flex-col items-start justify-start gap-1 p-3 text-left transition-all duration-200 ease-in-out hover:scale-[1.02] hover:shadow-md',
                                  testament.name === 'Antigo Testamento'
                                    ? 'border-blue-300 bg-blue-50/50 hover:bg-blue-100/70 dark:border-blue-700 dark:bg-blue-950/50 dark:hover:bg-blue-900/70'
                                    : 'border-emerald-300 bg-emerald-50/50 hover:bg-emerald-100/70 dark:border-emerald-700 dark:bg-emerald-950/50 dark:hover:bg-emerald-900/70',
                                  isSelected &&
                                    (testament.name === 'Antigo Testamento'
                                      ? 'bg-blue-100/70 dark:bg-blue-900/70'
                                      : 'bg-emerald-100/70 dark:bg-emerald-900/70')
                                )}
                                onClick={() => handleBookSelect(normalizedBook)}
                              >
                                <span className="text-base font-medium">{bookName}</span>
                                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                                  <div className="flex items-center gap-1">
                                    <BookText className="h-3 w-3" />
                                    <span>{totalVerses} versículos</span>
                                  </div>
                                  {hasAnnotations && (
                                    <div className="flex items-center gap-1">
                                      <Pencil className="h-3 w-3" />
                                      <span>Anotações</span>
                                    </div>
                                  )}
                                  {hasCitations && (
                                    <div className="flex items-center gap-1">
                                      <Quote className="h-3 w-3" />
                                      <span>Citações</span>
                                    </div>
                                  )}
                                  {lastReadChapter && lastReadVerse && (
                                    <div className="flex items-center gap-1">
                                      <Clock className="h-3 w-3" />
                                      <span>
                                        Última leitura: {lastReadChapter}: {lastReadVerse}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </Button>
                            )
                          })}
                        </div>
                      </div>
                    ))
                  )}
                </ScrollArea>
              </CardContent>
            </Card>
          </motion.div>
        ) : (
          // Chapter and Verses View
          <motion.div
            key="verse-view"
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 50 }}
            transition={{ duration: 0.3 }}
            className="h-full"
          >
            <Card className="h-full">
              <CardHeader className="flex-row items-center justify-between space-y-0">
                <div className="flex items-center gap-3">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={handleBackToBooks}
                    className="shrink-0"
                  >
                    <ArrowLeft className="h-5 w-5" />
                  </Button>
                  <div className="space-y-1">
                    <CardTitle className="text-2xl">{selectedBook.name}</CardTitle>
                    <CardDescription>Capítulo {selectedChapter?.number}</CardDescription>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => handleChapterChange('prev')}
                    disabled={selectedChapter?.number === 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => handleChapterChange('next')}
                    disabled={selectedChapter?.number === selectedBook.chapters.length}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="h-[calc(100%-100px)]">
                <div className="relative mb-4">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    placeholder="Buscar versículo..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <ScrollArea className="h-[calc(100%-60px)] pr-4">
                  {filteredVerses.length === 0 ? (
                    <p className="text-center text-muted-foreground">
                      Nenhum versículo encontrado.
                    </p>
                  ) : (
                    filteredVerses.map((verse) => {
                      const annotation =
                        selectedBook &&
                        selectedChapter &&
                        getAnnotation({
                          bookName: selectedBook.name,
                          chapterNumber: selectedChapter.number,
                          verseNumber: verse.number,
                        })
                      return (
                        <p key={verse.number} className="mb-4 text-lg leading-relaxed">
                          <sup className="mr-2 font-bold text-primary">{verse.number}</sup>
                          <span
                            className={cn(
                              'rounded-sm px-1',
                              annotation?.bgColor || '',
                              annotation?.textColor || '',
                              annotation?.darkBgColor || '',
                              annotation?.darkTextColor || ''
                            )}
                          >
                            {verse.text}
                          </span>
                          {annotation?.text && (
                            <span className="ml-2 text-sm italic text-muted-foreground">
                              (Anotado)
                            </span>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            className="ml-2 h-auto p-1 text-primary"
                            onClick={() => handleOpenAnnotationModal(verse)}
                          >
                            <Pencil className="h-3 w-3" />
                          </Button>
                        </p>
                      )
                    })
                  )}
                </ScrollArea>
              </CardContent>
            </Card>

            {/* Action Buttons (Placeholder for future features) */}
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="outline" onClick={handleAnnotateFirstVerse}>
                <MessageSquare className="mr-2 h-4 w-4" />
                Anotar
              </Button>
              <Button variant="outline" onClick={handleCiteVerse}>
                <Share2 className="mr-2 h-4 w-4" />
                Citar
              </Button>
            </div>
          </motion.div>
        )}
      </div>

      {/* Annotation Modal */}
      <Dialog open={showAnnotationModal} onOpenChange={setShowAnnotationModal}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Anotar Versículo</DialogTitle>
            <DialogDescription>
              Adicione ou edite sua anotação para este versículo.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="annotation-text">Sua Anotação</Label>
              <Textarea
                id="annotation-text"
                placeholder="Digite sua anotação aqui..."
                value={currentAnnotationText}
                onChange={(e) => setCurrentAnnotationText(e.target.value)}
                rows={5}
              />
            </div>
            <div className="grid gap-2">
              <Label>Cor da Anotação</Label>
              <div className="flex gap-2">
                {ANNOTATION_COLORS.map((colorOption) => (
                  <Button
                    key={colorOption.name}
                    variant="outline"
                    size="icon"
                    className={cn(
                      'h-8 w-8 rounded-full',
                      colorOption.bgColor,
                      colorOption.darkBgColor,
                      currentAnnotationColorOption.bgColor === colorOption.bgColor &&
                        'ring-2 ring-primary ring-offset-2'
                    )}
                    onClick={() => setCurrentAnnotationColorOption(colorOption)}
                    title={colorOption.name}
                  >
                    {currentAnnotationColorOption.bgColor === colorOption.bgColor && (
                      <Palette className="h-4 w-4 text-primary-foreground" />
                    )}
                  </Button>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleCancelAnnotation}>
              Cancelar
            </Button>
            <Button onClick={handleSaveAnnotation}>Salvar Anotação</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
